// filepath: apps/web/src/runtime/useIsMobile.ts
//
// FR-UI-NFR-3 — Responsive Layout hook.
//
// Detects whether the viewport is at or below the mobile breakpoint (375px).
// Used to switch between desktop row layout and mobile card layout in the
// task inbox, and to apply mobile-specific styling to form fields.
//
// AC-1: Task inbox switches to single-column card layout at 375px.
// AC-2: Form fields render without horizontal scrolling at 375px.
// AC-3: Touch targets meet 48×48dp minimum on mobile.
// AC-4: Reference picker opens as bottom sheet on mobile.
// AC-5: Text wraps without truncation at 375px.

import { useState, useEffect } from "react";

/** Mobile breakpoint width in pixels (375px as per FR-UI-NFR-3). */
export const MOBILE_BREAKPOINT = 375;

/**
 * Returns true when the viewport width is at or below the mobile
 * breakpoint (375px). Re-evaluates on window resize.
 *
 * SSR-safe: defaults to false on the server (window is unavailable).
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function checkViewport(): void {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    }

    // Initial check on mount.
    checkViewport();

    // Re-evaluate on resize.
    window.addEventListener("resize", checkViewport, { passive: true });

    return () => {
      window.removeEventListener("resize", checkViewport);
    };
  }, []);

  return isMobile;
}

/**
 * Minimum touch target size in pixels (48dp × 1px/dip at 100% zoom).
 * AC-3: All interactive controls must have at least this touch target.
 */
export const MIN_TOUCH_TARGET_PX = 48;
